"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/lib/utils";

const Drawer = ({
  shouldScaleBackground = true,
  preventClose = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
  /** Prevent accidental closing by dragging (useful for forms) */
  preventClose?: boolean;
}) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    // When preventClose is true, disable dismissible to prevent accidental swipe-to-close
    dismissible={preventClose ? false : undefined}
    // Increase the threshold for closing - user needs to drag more to close
    closeThreshold={preventClose ? 0.5 : 0.25}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerPortal = DrawerPrimitive.Portal;
const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    nestedLevel?: number;
  }
>(({ className, nestedLevel = 0, style, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 bg-black/50 backdrop-grayscale", className)}
    style={{ zIndex: 1001 + nestedLevel * 100, ...style }}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    showHandle?: boolean;
    overlayClassName?: string;
    /** Show a larger, more visible handle for better UX */
    largeHandle?: boolean;
    /** Level of nesting for z-index handling (0 = top level, 1 = nested, etc.) */
    nestedLevel?: number;
  }
>(
  (
    {
      className,
      children,
      showHandle = true,
      largeHandle = false,
      overlayClassName,
      nestedLevel = 0,
      style,
      ...props
    },
    ref,
  ) => {
    const contentZIndex = 1002 + nestedLevel * 100;

    return (
      <DrawerPortal>
        <DrawerOverlay className={overlayClassName} nestedLevel={nestedLevel} />
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            "fixed inset-x-0 bottom-0 mt-24 flex h-auto flex-col rounded-t-[10px] bg-popover text-popover-foreground",
            className,
          )}
          style={{ zIndex: contentZIndex, ...style }}
          {...props}
        >
          {showHandle && (
            <div
              className={cn(
                "mx-auto mt-4 rounded-full bg-muted",
                largeHandle ? "h-1.5 w-15" : "h-2 w-25",
              )}
            />
          )}
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
